import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { BookOpen, Check, ChevronsUpDown, Heart, Layers3, Share2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { toast } from "sonner";
import { api } from "../lib/api";
import { pcImageUrl, pcImageFallback } from "../lib/pcImage";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { cardTotalValue } from "../lib/conditions";
import CardTile from "../components/CardTile";
import TradePingDialog from "../components/TradePingDialog";
import ReportDialog from "../components/ReportDialog";
import ChaseHeartButton from "../components/ChaseHeartButton";
import PublicCardDialog from "../components/PublicCardDialog";

export default function CollectorBinder() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const currencyCtx = useCurrency();
  const fmt = currencyCtx?.format || ((v) => (v == null ? null : `$${v.toFixed(2)}`));
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState(searchParams.get("collection") || "all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/community/binder/${userId}`)
      .then((res) => setData(res.data))
      .catch(() => setError(true));
  }, [userId]);

  const cards = useMemo(() => {
    if (!data) return [];
    if (collectionFilter === "all") return data.cards;
    return data.cards.filter((c) => c.collection_id === collectionFilter);
  }, [data, collectionFilter]);

  const totalValue = useMemo(
    () => (data ? data.cards.reduce((sum, c) => sum + (cardTotalValue(c) || 0), 0) : 0),
    [data]
  );

  if (error) {
    return (
      <div className="min-h-screen bg-[#05060f] text-white flex items-center justify-center">
        <p data-testid="collector-binder-private" className="text-zinc-500">This binder is private or doesn't exist.</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#05060f] text-white flex items-center justify-center"><p className="text-zinc-500 animate-pulse">Loading binder...</p></div>;
  }

  const isMe = user?.user_id === userId;

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <BookOpen className="w-6 h-6 text-[#A78BFA]" />
          <h1 data-testid="collector-binder-name" className="font-display text-3xl sm:text-4xl font-bold">{data.display_name}</h1>
          <button
            data-testid="collector-share-button"
            onClick={async () => {
              const url = `${window.location.origin}/api/collector/${userId}/share`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Binder link copied — paste it anywhere");
              } catch {
                toast.error("Copy failed — link: " + url);
              }
            }}
            className="inline-flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full border border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
        <p className="text-sm text-zinc-500 mb-6">
          {data.cards.length} cards{data.show_value !== false && <> · collection value {fmt(totalValue)}</>}
          {isMe && <span className="ml-2 text-[#A78BFA]">(this is your public binder — <Link to="/settings" className="underline">settings</Link>)</span>}
        </p>

        {data.chase.length > 0 && (
          <section data-testid="collector-chase-deck" className="mb-8 border border-pink-400/20 bg-pink-500/5 rounded-2xl p-4">
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-pink-400 fill-pink-400" /> Chase Deck
              <span className="text-xs text-zinc-500 font-normal">cards {isMe ? "you want" : "they want"} — got one? Ping a trade!</span>
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {data.chase.map((item) => (
                <div key={item.id} data-testid={`collector-chase-item-${item.id}`} className="w-24 shrink-0">
                  {item.image_url ? (
                    <img src={pcImageUrl(item.image_url.startsWith("http") ? item.image_url : `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`, "240")} onError={pcImageFallback} alt={item.name} className="w-24 aspect-[3/4] object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-24 aspect-[3/4] flex items-center justify-center text-[9px] text-zinc-700 border border-white/10 rounded-lg text-center px-1">{item.name}</div>
                  )}
                  <p className="text-[10px] text-zinc-400 mt-1 truncate">{item.name}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {data.collections.length > 0 && (
          <div className="mb-6">
            <CollectionFilterDropdown
              collections={data.collections}
              cards={data.cards}
              value={collectionFilter}
              onChange={setCollectionFilter}
            />
          </div>
        )}

        {cards.length === 0 ? (
          <p className="text-zinc-600 text-sm py-12 text-center">No cards here yet.</p>
        ) : (
          <div data-testid="collector-card-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {cards.map((card, i) => (
              <div key={card.id} className="relative">
                <CardTile card={card} index={i} viewOnly onView={() => setSelected(card)} />
                {user && !isMe && (
                  <div className="absolute top-2 left-2 z-10 flex gap-1.5">
                    <TradePingDialog cardId={card.id} cardName={card.name} ownerName={data.display_name} />
                    <ReportDialog cardId={card.id} />
                    <ChaseHeartButton card={card} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <PublicCardDialog
          card={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
          fmt={fmt}
          ownerName={data.display_name}
          actions={selected && user && !isMe ? (
            <TradePingDialog cardId={selected.id} cardName={selected.name} ownerName={data.display_name} />
          ) : null}
        />
      </div>
    </div>
  );
}

function CollectionFilterDropdown({ collections, cards, value, onChange }) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => {
    const opts = [{ id: "all", name: "All sets", count: cards.length }];
    collections.forEach((c) => {
      const count = cards.filter((card) => card.collection_id === c.id).length;
      if (count) opts.push({ id: c.id, name: c.name, count });
    });
    return opts;
  }, [collections, cards]);

  const current = options.find((o) => o.id === value) || options[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="collector-filter-dropdown"
          role="combobox"
          aria-expanded={open}
          className="inline-flex items-center justify-between gap-3 min-w-[260px] max-w-full text-sm px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10 hover:border-[#A78BFA]/40 transition-colors"
        >
          <span className="flex items-center gap-2 truncate">
            <Layers3 className="w-4 h-4 text-[#A78BFA] shrink-0" />
            <span className="truncate">{current.name}</span>
            <span className="text-zinc-500 shrink-0">({current.count})</span>
          </span>
          <ChevronsUpDown className="w-4 h-4 text-zinc-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] p-0 border-white/10 bg-[#0b0d1a] text-zinc-200">
        <Command className="bg-transparent text-zinc-200">
          <CommandInput data-testid="collector-filter-search" placeholder="Search sets..." className="text-zinc-200 placeholder:text-zinc-600" />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-6 text-center text-sm text-zinc-500">No matching set.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.name}
                  data-testid={`collector-filter-option-${o.id}`}
                  onSelect={() => { onChange(o.id); setOpen(false); }}
                  className="cursor-pointer text-zinc-300 aria-selected:bg-[#A78BFA]/15 aria-selected:text-white"
                >
                  <Check className={`w-4 h-4 mr-2 shrink-0 ${value === o.id ? "text-[#A78BFA] opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{o.name}</span>
                  <span className="ml-auto text-xs text-zinc-500 pl-2 shrink-0">{o.count}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
